import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface EmptyStateProps {
  /** Primary line. RELATIONAL voice — describe the user's world, not the data model.
   *  ✓ "Nothing waiting on you right now."  ✗ "No tasks found." / "0 messages."
   *  Present tense, sentence case, ends with a period. No exclamation points.
   *  Imperatives ("Create your first…") belong in `action`, never here. */
  message: string;
  /** Secondary line — understated expectation-setting ("It'll appear here when…"). */
  subMessage?: string;
  /** Action affordance (link/button) under the message. Use for first-run CTAs or a
   *  clear-filters link. Omit for pure quiet-states. */
  action?: React.ReactNode;
  /** Illustration above the message. ICON RULE: never a raw emoji; use a lucide icon
   *  from components/Icons ONLY when the domain is visually identifiable and the icon
   *  reduces confusion (e.g. SearchIcon on a filtered-empty result). Most empty states
   *  should pass NO icon — the dashed card already reads as quiet. */
  icon?: React.ReactNode;
  /** Intent variant (contract §7). Guidance-first:
   *  - 'default'   quiet absence, no direction implied (dashed card).
   *  - 'filtered'  data exists but nothing matches — offer a clear-filters `action`.
   *  - 'first-run' nothing has ever existed — encouraging; `action` = create CTA;
   *                renders a solid (not dashed) border to read as an invitation. */
  variant?: 'default' | 'filtered' | 'first-run';
  /** Role/accent tint passed by the caller (Option A — the primitive stays auth-free).
   *  Pass a border-tint class from the caller's useIdentity() result, e.g.
   *  `roleClass="border-accent/40"`. Merged onto the root. */
  roleClass?: string;
  className?: string;
  /** Forwarded to the root div for test selection. */
  'data-testid'?: string;
}

/**
 * The one quiet-state component (contract §7): a dashed border card in relational
 * voice. Do NOT hand-roll empty states (raw emoji + h3 + p) — use this. Do NOT
 * collapse loading/error into it; those are separate states.
 */
export function EmptyState({
  message,
  subMessage,
  action,
  icon,
  variant = 'default',
  roleClass,
  className,
  'data-testid': testId,
}: EmptyStateProps) {
  // first-run reads as an invitation (solid border); default/filtered stay quiet (dashed).
  const borderStyle = variant === 'first-run' ? 'border-solid' : 'border-dashed';
  return (
    <div
      data-testid={testId}
      className={twMerge(
        'border border-border-light rounded-[14px] px-3.5 py-[18px] text-center',
        borderStyle,
        roleClass,
        className
      )}
    >
      {icon && <div className="mb-3 opacity-50 flex justify-center">{icon}</div>}
      <p className="text-sm text-text-secondary">{message}</p>
      {subMessage && (
        <p className="text-xs text-text-tertiary mt-1">{subMessage}</p>
      )}
      {action && <div className="mt-3 flex justify-center">{action}</div>}
    </div>
  );
}
